// =====================================================
// ONEFORM - TIPOS TYPESCRIPT
// =====================================================

// Tipos de campos disponíveis no formulário
export type FieldType =
  | 'text'
  | 'email'
  | 'phone'
  | 'textarea'
  | 'select'
  | 'multiselect'
  | 'checkbox'
  | 'radio'
  | 'date'
  | 'datetime'
  | 'time'
  | 'file'
  | 'number'
  | 'url'
  | 'heading'
  | 'paragraph'
  // Premium fields (Story 1.1)
  | 'welcome_screen'
  | 'thank_you_screen'
  | 'opinion_scale'
  | 'nps'
  | 'rating'
  | 'picture_choice'
  | 'yes_no'
  | 'statement'
  | 'redirect_url';

// Non-input field types that don't contribute form data
export const NON_INPUT_FIELD_TYPES: FieldType[] = [
  'heading', 'paragraph', 'welcome_screen', 'thank_you_screen', 'statement', 'redirect_url',
];

// Opção de campo com suporte a scoring (Story 1.1)
export interface FieldOptionScore {
  variableId: string;
  points: number;
}

export interface FieldOption {
  label: string;
  value: string;
  scores?: FieldOptionScore[];
}

// Scoring types (Story 1.1)
export interface ScoreRange {
  min: number;
  max: number;
  label: string;
  color?: string;
  icon?: string;
  message?: string;
}

export interface ScoreVariable {
  id: string;
  name: string;
  ranges: ScoreRange[];
}

export interface ScoringConfig {
  enabled: boolean;
  variables: ScoreVariable[];
  showResultToRespondent: boolean;
}

// Field-specific config interfaces (Story 1.1)
export interface OpinionScaleConfig {
  minValue?: number;       // default: 1
  maxValue?: number;       // default: 10
  minLabel?: string;       // e.g., "Nada provável"
  maxLabel?: string;       // e.g., "Muito provável"
}

export interface RatingConfig {
  maxRating?: number;      // default: 5
  iconType?: 'star' | 'heart' | 'emoji';
}

export interface PictureChoiceConfig {
  images: { url: string; label: string }[];
  multiSelect?: boolean;
}

export interface WelcomeScreenConfig {
  buttonText?: string;     // default: "Começar"
  showDescription?: boolean;
}

export interface ThankYouScreenConfig {
  showShareButton?: boolean;
  redirectDelay?: number;  // seconds before redirect
}

export interface RedirectUrlConfig {
  url?: string;
  delay?: number;           // seconds before redirect (0-10)
  openInNewTab?: boolean;
  hideCountdown?: boolean;  // hides "Redirecionando em Xs..." timer (undefined → visible for backward-compat)
}

export type OptionLayout = 'vertical' | 'horizontal' | 'grid-2' | 'grid-3';
export type FieldWidth = 'full' | 'half' | 'third';

// Field types que suportam `config.optionLayout` — shared desktop/mobile
// (mobile-ui.6.G: extraído de SlidePropertiesPanel pra evitar drift).
export const LAYOUT_OPTION_TYPES: FieldType[] = [
  'radio', 'checkbox', 'multiselect', 'picture_choice',
];

// =====================================================
// MULTI-QUESTION PAGE TYPES (Epic 5 - Story 5.1)
// =====================================================

// Página que agrupa múltiplos campos em um único slide no modo Typeform
export interface FormPage {
  id: string;
  title?: string;
  /** Descrição opcional renderizada abaixo do title em multi-field pages. saas-1.23.C AC9. */
  description?: string;
  columns: 1 | 2 | 3;
  order: number;
}

// Field types que NÃO podem ser adicionados a páginas (ficam como slides individuais)
export const PAGE_EXCLUDED_FIELD_TYPES: FieldType[] = [
  'welcome_screen', 'thank_you_screen', 'redirect_url',
];

// Layout config shared across field types
export interface FieldLayoutConfig {
  optionLayout?: OptionLayout;
  backgroundImage?: string;
  backgroundBlur?: number;     // px, default 0 (Story saas-1.6)
  backgroundOpacity?: number;  // 0–100, default 100 (Story saas-1.6)
  allowOther?: boolean;
}

export type FieldConfig = (OpinionScaleConfig | RatingConfig | PictureChoiceConfig | WelcomeScreenConfig | ThankYouScreenConfig | RedirectUrlConfig) & FieldLayoutConfig;

// =====================================================
// RULE EDITOR TYPES (Story saas-2.5.6)
// =====================================================

export type RuleOperator = 'is' | 'is_not';

export interface RuleCondition {
  id: string;
  fieldId: string;
  operator: RuleOperator;
  value: string | string[];
}

export type CalculationOperation = 'add' | 'subtract' | 'multiply' | 'divide';

// Discriminated union: action targets are mutually exclusive (Story 5.7)
// - 'jump_to' → targets a field; resolves to the page containing the field for multi-question forms
// - 'jump_to_page' → targets a page directly
export type RuleLogicAction =
  | { type: 'jump_to'; targetFieldId: string }
  | { type: 'jump_to_page'; targetPageId: string };

export interface RuleCalculationAction {
  type: 'calculation';
  operation: CalculationOperation;
  value: number;
  variableId: string;
}

export type RuleAction = RuleLogicAction | RuleCalculationAction;

export interface FieldRule {
  id: string;
  conditionType: 'if' | 'always';
  conditions: RuleCondition[];
  logicOperator: 'and' | 'or';
  actions: RuleAction[];
}

// =====================================================
// FLOW EDITOR TYPES (Story 3.2)
// =====================================================

export interface FlowNodeData {
  fieldId: string;
  position: { x: number; y: number };
  width: number;
  height: number;
}

export interface FlowConnectionData {
  id: string;
  sourceFieldId: string;
  targetFieldId: string;
  condition?: Condition;
  isDefault: boolean;
}

export interface FlowEditorState {
  nodes: FlowNodeData[];
  connections: FlowConnectionData[];
  zoom: number;
  pan: { x: number; y: number };
  selectedNodeId: string | null;
  selectedConnectionId: string | null;
}

export type FlowValidationIssueType = 'dead_end' | 'loop' | 'unreachable' | 'orphan_connection';

export interface FlowValidationIssue {
  type: FlowValidationIssueType;
  fieldIds: string[];
  message: string;
}

// Utility: normalize string option to FieldOption (backward compat)
export function normalizeFieldOption(option: string | FieldOption): FieldOption {
  if (typeof option === 'string') {
    return { label: option, value: option };
  }
  return option;
}

// Configuração de validação de campo
export interface FieldValidation {
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  patternMessage?: string;
  fileTypes?: string[];      // ['image/*', 'application/pdf']
  maxFileSize?: number;      // em bytes
}

// Condition for advanced branching (Story 1.1)
export interface Condition {
  fieldId: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'is_empty' | 'is_not_empty' | 'greater_than' | 'less_than';
  value?: string | string[];
  jumpTo?: string; // Per-condition jump target for multi-branch flows
}

// Lógica condicional para exibição de campos
export interface ConditionalLogic {
  // Legacy fields (backward compatible)
  dependsOn?: string;
  operator?: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'is_empty' | 'is_not_empty';
  value?: string | string[];
  // New branching fields (Story 1.1)
  action?: 'show' | 'hide' | 'jump_to';
  jumpTo?: string;
  conditions?: Condition[];
  logicOperator?: 'and' | 'or';
}

// Field alignment type (Story saas-1.9 — exported from inline declaration for reuse)
export type FieldAlignment = 'left' | 'center' | 'right';

// Definição de um campo do formulário
export interface FormField {
  id: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  helpText?: string;
  required: boolean;
  options?: (string | FieldOption)[];    // Backward compat: string[] still works, FieldOption[] for scoring
  defaultValue?: string | string[];
  validation?: FieldValidation;
  conditionalLogic?: ConditionalLogic;
  rules?: FieldRule[];                   // Rule Editor rules (Story saas-2.5.6)
  config?: FieldConfig;                  // Field-specific config (Story 1.1)
  alignment?: FieldAlignment;            // Slide alignment (Story saas-2.5.1)
  block?: string;                        // Agrupamento lógico (ex: "Identificação", "Projeto")
  width?: FieldWidth;                    // Largura do campo no layout (full, half, third)
  pageId?: string;                       // ID da página que contém este campo (Epic 5)
}

// Configuração de tema visual por formulário
export interface ThemeConfig {
  backgroundColor: string;
  textColor: string;
  fontFamily: string;
  buttonBgColor: string;
  buttonTextColor: string;
  buttonBorderRadius: number;
  buttonStyle: 'filled' | 'outlined' | 'ghost';
  buttonSize?: 'sm' | 'md' | 'lg';
  buttonPaddingX?: number;
  buttonPaddingY?: number;
  inputStyle?: 'outlined' | 'underline' | 'filled';
  inputBgColor?: string;
  inputBorderColor?: string;
  inputBorderRadius?: number;
}

export const DEFAULT_THEME_CONFIG: ThemeConfig = {
  backgroundColor: '#0a0a0a',
  textColor: '#ffffff',
  fontFamily: 'Inter',
  buttonBgColor: '#3b82f6',
  buttonTextColor: '#ffffff',
  buttonBorderRadius: 9999,
  buttonStyle: 'filled',
  inputStyle: 'outlined',
  inputBgColor: 'transparent',
  inputBorderColor: '',
  inputBorderRadius: 12,
};

// Webhook event types (Story 5.6)
export type WebhookEventType = 'response.complete' | 'response.partial' | 'response.all';

// Configuração de salvamento parcial (Story 5.1)
export type PartialNotifyMode = 'none' | 'partial_only' | 'complete_only' | 'all';

export interface PartialSubmitConfig {
  enabled: boolean;
  cookieExpiryDays?: number;      // default: 30
  notifyMode?: PartialNotifyMode; // default: 'complete_only'
}

// Configurações gerais do formulário
export interface FormSettings {
  showProgressBar?: boolean;
  /** @deprecated Use partialSubmit.enabled instead */
  allowSave?: boolean;
  notifyOnSubmit?: boolean;
  notifyEmail?: string;
  submitButtonText?: string;
  nextButtonText?: string;
  showBranding?: boolean;
  themeConfig?: ThemeConfig;
  displayMode?: 'classic' | 'typeform';  // default: 'typeform' (Multi-Step Form)
  scoring?: ScoringConfig;               // default: { enabled: false, variables: [], showResultToRespondent: false }
  logoUrl?: string;                      // URL da imagem do logo no Supabase Storage
  logoPosition?: LogoPosition;
  logoMargin?: number;                   // Distância do logo às bordas (px, default 16, range 0–64) — Story saas-1.6
  logoScale?: number;                    // Escala do logo (percentual 50–200, default 100 quando undefined) — Story saas-1.7
  partialSubmit?: PartialSubmitConfig;   // Salvamento parcial (Story 5.1)
  redirectUrl?: string;                  // URL pra onde redirecionar após submit (http/https). Substitui form.redirect_url top-level (que persiste como fallback).
}

// Logo position enum (Story saas-1.6): 4 → 7 valores
// middle-center renderiza INLINE acima da pergunta; as outras 6 renderizam absolute com logoMargin.
export type LogoPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'middle-center'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

// Definição completa de um formulário
export interface Form {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  slug?: string;
  fields: FormField[];
  pages?: FormPage[];                    // Multi-question page grouping (Epic 5)
  settings: FormSettings;
  is_published: boolean;
  is_template: boolean;
  thank_you_message: string;
  redirect_url?: string;
  created_at: string;
  updated_at: string;
  // Plan tier do owner — exposto pelo hook de fetch (BP-1, @Dara). Opcional até BP-1 ship.
  // BP-6 OneFormBadge fail-closed trata undefined como 'free'.
  owner_plan_tier?: 'free' | 'pro' | 'business' | null;
  // Campos computados (não vêm do DB)
  response_count?: number;
  last_response_at?: string;
}

// Status de uma resposta
export type ResponseStatus = 'new' | 'in_progress' | 'completed';

// Resposta de um formulário
export interface FormResponse {
  id: string;
  form_id: string;
  data: Record<string, any>;
  scores?: Record<string, {
    totalPoints: number;
    range: {
      label: string;
      color?: string;
      icon?: string;
      message?: string | null;
    } | null;
  }> | null;
  status: ResponseStatus;
  internal_notes?: string;
  session_token?: string;             // Partial submit session (Story 5.1)
  last_field_id?: string;             // Last answered field ID for resume (Story 5.1)
  created_at: string;
  updated_at: string;
  // Relacionamentos (opcionais, quando populados)
  form?: Form;
}

// Papel do usuário no sistema
export type UserRole = 'admin' | 'collaborator';

// Perfil de usuário
export interface UserProfile {
  id: string;
  email: string;
  full_name?: string;
  avatar_url?: string;
  username?: string;
  role: UserRole;
  is_superadmin?: boolean;
  is_exempt?: boolean;
  stripe_customer_id?: string;
  trial_ends_at?: string;
  created_at: string;
  updated_at?: string;
}

// =====================================================
// SUBSCRIPTION / BILLING TYPES
// =====================================================

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused';

export interface Subscription {
  id: string;
  user_id: string;
  stripe_subscription_id: string;
  stripe_customer_id: string;
  status: SubscriptionStatus;
  price_id: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  created_at: string;
  updated_at: string;
}

// Sessão do usuário autenticado
export interface AuthSession {
  user: UserProfile | null;
  loading: boolean;
  error: string | null;
}

// =====================================================
// TIPOS PARA O FORM BUILDER
// =====================================================

// Paleta de campos disponíveis no builder
export interface FieldPaletteItem {
  type: FieldType;
  label: string;
  icon: string;
  category: 'contact_info' | 'text_media' | 'choice' | 'rating_ranking' | 'other' | 'layout';
  defaultConfig: Partial<FormField>;
}

// =====================================================
// TIPOS PARA FILTROS E BUSCA
// =====================================================

export interface FormsFilter {
  search?: string;
  status?: 'all' | 'published' | 'draft';
  isTemplate?: boolean;
  sortBy?: 'created_at' | 'updated_at' | 'title' | 'response_count';
  sortOrder?: 'asc' | 'desc';
}

export interface ResponsesFilter {
  search?: string;
  status?: ResponseStatus | 'all';
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'created_at' | 'status' | 'score';
  sortOrder?: 'asc' | 'desc';
  scoreRange?: string;
  scoreVariableId?: string;
}

// =====================================================
// TIPOS PARA TEMPLATES
// =====================================================

export interface FormTemplate {
  id: string;
  title: string;
  description: string;
  category: string;
  fields: FormField[];
  settings: FormSettings;
  preview_image?: string;
}

// Templates pré-definidos do sistema
export const DEFAULT_TEMPLATES: FormTemplate[] = [
  {
    id: 'briefing-identidade',
    title: 'Briefing de Identidade Visual',
    description: 'Formulário completo para coleta de informações de projetos de identidade visual',
    category: 'Design',
    fields: [],
    settings: { showProgressBar: true, showBranding: true },
  },
  {
    id: 'briefing-site',
    title: 'Briefing de Website',
    description: 'Coleta informações essenciais para desenvolvimento de websites',
    category: 'Web',
    fields: [],
    settings: { showProgressBar: true, showBranding: true },
  },
  {
    id: 'briefing-redes',
    title: 'Briefing de Redes Sociais',
    description: 'Questionário para projetos de design para redes sociais',
    category: 'Social Media',
    fields: [],
    settings: { showProgressBar: true, showBranding: true },
  },
  {
    id: 'feedback-cliente',
    title: 'Feedback de Cliente',
    description: 'Formulário para coletar avaliações e depoimentos',
    category: 'Feedback',
    fields: [],
    settings: { showProgressBar: false, showBranding: true },
  },
];

// =====================================================
// TIPOS PARA API/HOOKS
// =====================================================

export interface CreateFormInput {
  title: string;
  description?: string;
  slug?: string;
  fields?: FormField[];
  pages?: FormPage[] | null;
  settings?: FormSettings;
  is_template?: boolean;
  is_published?: boolean;
}

export interface UpdateFormInput {
  title?: string;
  description?: string;
  slug?: string | null;
  fields?: FormField[];
  pages?: FormPage[] | null;
  settings?: FormSettings;
  is_published?: boolean;
  is_template?: boolean;
  thank_you_message?: string;
  redirect_url?: string;
}

export interface SubmitResponseInput {
  form_id: string;
  data: Record<string, any>;
  scores?: Record<string, any> | null;
}

export interface UpdateResponseInput {
  status?: ResponseStatus;
  internal_notes?: string;
}

// =====================================================
// TIPOS PARA MARKETING E TRACKING
// =====================================================

// Configurações do Google Tag Manager
export interface GTMSettings {
  enabled: boolean;
  containerId: string; // GTM-XXXXXXX
}

// Configurações do Google Analytics 4
export interface GA4Settings {
  enabled: boolean;
  measurementId: string; // G-XXXXXXXXXX
}

// Configurações do Meta Pixel (Facebook)
export interface MetaPixelSettings {
  enabled: boolean;
  pixelId: string; // Número do Pixel
  advancedMatching: boolean; // Correspondência avançada
}

// Configurações do Meta Conversions API (CAPI)
export interface MetaCAPISettings {
  enabled: boolean;
  accessToken: string; // Token de acesso
  testEventCode?: string; // Código de teste (opcional)
}

// Eventos personalizados de tracking
export interface TrackingEvent {
  id: string;
  name: string;
  platform: 'gtm' | 'ga4' | 'meta' | 'all';
  trigger: 'page_view' | 'form_submit' | 'button_click' | 'scroll' | 'custom';
  parameters?: Record<string, string>;
  enabled: boolean;
}

// Configurações completas de marketing
export interface MarketingSettings {
  id: string;
  user_id: string;
  gtm: GTMSettings;
  ga4: GA4Settings;
  metaPixel: MetaPixelSettings;
  metaCAPI: MetaCAPISettings;
  customEvents: TrackingEvent[];
  cookieConsent: boolean; // Exigir consentimento de cookies
  created_at: string;
  updated_at: string;
}

// Input para criar/atualizar configurações de marketing
export interface UpdateMarketingInput {
  gtm?: Partial<GTMSettings>;
  ga4?: Partial<GA4Settings>;
  metaPixel?: Partial<MetaPixelSettings>;
  metaCAPI?: Partial<MetaCAPISettings>;
  customEvents?: TrackingEvent[];
  cookieConsent?: boolean;
}

// Valores padrão para configurações de marketing
export const DEFAULT_MARKETING_SETTINGS: Omit<MarketingSettings, 'id' | 'user_id' | 'created_at' | 'updated_at'> = {
  gtm: {
    enabled: false,
    containerId: '',
  },
  ga4: {
    enabled: false,
    measurementId: '',
  },
  metaPixel: {
    enabled: false,
    pixelId: '',
    advancedMatching: false,
  },
  metaCAPI: {
    enabled: false,
    accessToken: '',
    testEventCode: '',
  },
  customEvents: [],
  cookieConsent: true,
};
